import {
  AppstoreAddOutlined,
  ArrowDownOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  ArrowUpOutlined,
  ColumnWidthOutlined,
  EyeInvisibleOutlined,
  HolderOutlined,
  PlusOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import type { WorkspaceCabinet, WorkspaceCatalogItem, WorkspaceSection } from "@wms/domain";
import { Alert, App, Button, Empty, Grid, Input, List, Modal, Skeleton, Space, Tag, Typography } from "antd";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent, PointerEvent, ReactNode } from "react";
import { errorMessage } from "../api/client";
import { HelpHint } from "../components/HelpHint";
import {
  addWidget,
  canMove,
  forSave,
  move,
  moveTo,
  positionText,
  removeWidget,
  setSize,
  splitHidden,
  standardLayout,
  type Direction,
  type Placement,
} from "./layout";
import { conflictOf, useWorkspace } from "./useWorkspace";
import "./desktop.css";

/** What a screen draws for one widget: the body and, outside the edit mode, the links of its header. */
export interface WidgetView {
  body: ReactNode;
  extra?: ReactNode;
}

interface Props {
  cabinet: WorkspaceCabinet;
  /** The codes this screen knows how to draw; any other code of the saved desktop is kept, never drawn. */
  codes: readonly string[];
  render: (code: string) => WidgetView;
  /** «Мини-пример» of the catalogue: what the widget shows, in a few words. */
  previews?: Record<string, string>;
  /** The screen of HEAD when the own desktop cannot be read — work goes on without the personal layout. */
  fallback: ReactNode;
  /** The heading of the page above the toolbar (the fallback has its own). */
  header?: ReactNode;
  /** Shown in the normal mode next to «Настроить рабочий стол». */
  toolbarExtra?: ReactNode;
}

const MOVES: { dir: Direction; label: string; icon: ReactNode }[] = [
  { dir: "up", label: "Выше", icon: <ArrowUpOutlined /> },
  { dir: "down", label: "Ниже", icon: <ArrowDownOutlined /> },
  { dir: "left", label: "Влево", icon: <ArrowLeftOutlined /> },
  { dir: "right", label: "Вправо", icon: <ArrowRightOutlined /> },
];
const KEY_DIRECTIONS: Record<string, Direction> = { ArrowUp: "up", ArrowDown: "down", ArrowLeft: "left", ArrowRight: "right" };
const HINT_ID = "desktop-move-hint";

/** UXW-21: in the edit mode the content of a widget is a picture — its links and buttons neither open nor drag. */
function WidgetBody({ editing, children }: { editing: boolean; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.toggleAttribute("inert", editing);
  }, [editing]);
  return (
    <div
      ref={ref}
      className="wms-widget-body"
      onClickCapture={(e) => {
        if (editing) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
    >
      {children}
    </div>
  );
}

/**
 * ТЗ «Рабочие столы» 31.10.2026 (UXW-18…26, CW-10…19): the desktop of staff and of the client cabinet from widgets of
 * the allowed catalogue. «Настроить рабочий стол» opens the edit mode: drag a widget by its handle (mouse or finger),
 * or move it with «Выше / Ниже / Влево / Вправо», or from the keyboard on the handle; add from the catalogue, remove,
 * show the standard set; «Сохранить» keeps the own layout, «Отмена» returns the previous one. Only the order and the
 * sizes are stored — the data of every widget comes from its own allowed API.
 */
export function Desktop({ cabinet, codes, render, previews = {}, fallback, header, toolbarExtra }: Props) {
  const { message } = App.useApp();
  const workspace = useWorkspace(cabinet, true);
  const screens = Grid.useBreakpoint();
  // the moves follow what the screen shows: two columns from «md» (as the CSS grid), one column below it
  const columns = screens.md === false ? 1 : 2;
  const payload = workspace.data;

  const catalog = useMemo(() => (payload?.catalog ?? []).filter((c) => codes.includes(c.code)), [payload, codes]);
  const catalogCodes = useMemo(() => catalog.map((c) => c.code), [catalog]);
  const byCode = useMemo(() => new Map<string, WorkspaceCatalogItem>(catalog.map((c) => [c.code, c])), [catalog]);
  const saved = useMemo(
    () => (payload ? splitHidden(payload.workspace.desktop.widgets, payload.hidden, catalogCodes) : { visible: [], kept: [] }),
    [payload, catalogCodes],
  );

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Placement[]>([]);
  const [kept, setKept] = useState<Placement[]>([]);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [announce, setAnnounce] = useState("");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<WorkspaceSection | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const [grabbed, setGrabbed] = useState<string | null>(null);
  const [focus, setFocus] = useState<{ code: string; part: string } | null>(null);

  const controls = useRef(new Map<string, HTMLElement>());
  const cells = useRef(new Map<string, HTMLElement>());
  const keyMove = useRef<{ code: string; origin: Placement[] } | null>(null);
  const drag = useRef<{ code: string; pointerId: number; x: number; y: number; active: boolean; origin: Placement[]; over: string | null } | null>(null);
  const draftRef = useRef<Placement[]>(draft);
  draftRef.current = draft;

  const list = editing ? draft : saved.visible;
  const label = (code: string) => byCode.get(code)?.label ?? code;

  // UXW-22: the moved widget keeps the focus — the same button, or its handle when that button can go no further
  useLayoutEffect(() => {
    if (!focus) return;
    const own = controls.current.get(`${focus.code}:${focus.part}`) as HTMLButtonElement | undefined;
    const target = own && !own.disabled ? own : controls.current.get(`${focus.code}:handle`);
    target?.focus();
    setFocus(null);
  }, [focus, list]);

  // Escape during a drag returns the widget where it was (UXW-22)
  useEffect(() => {
    if (!dragging) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== "Escape" || !drag.current) return;
      e.preventDefault();
      const d = drag.current;
      drag.current = null;
      setDraft(d.origin);
      setDragging(null);
      setAnnounce(`Перемещение отменено: виджет «${label(d.code)}» на прежнем месте`);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragging]);

  const say = (code: string, next: Placement[], prefix?: string) => {
    const index = next.findIndex((w) => w.code === code);
    const text = positionText(label(code), index, next.length);
    setAnnounce(prefix ? `${prefix}. ${text}` : text);
  };

  const begin = (initial?: Placement[]) => {
    setDraft(initial ?? saved.visible);
    setKept(saved.kept);
    setEditing(true);
    setSaveError(null);
    setAnnounce("Режим настройки рабочего стола: перемещайте виджеты за ручку или кнопками, затем нажмите «Сохранить»");
  };
  const finish = () => {
    setEditing(false);
    setDraft([]);
    setGrabbed(null);
    setDragging(null);
    keyMove.current = null;
    drag.current = null;
  };
  const cancel = () => {
    finish();
    setSaveError(null);
    setAnnounce("Изменения отменены: рабочий стол прежний");
  };

  const step = (code: string, direction: Direction, part: string) => {
    const index = draft.findIndex((w) => w.code === code);
    const next = move(draft, index, direction, columns);
    if (next === draft) return;
    setDraft(next);
    say(code, next);
    setFocus({ code, part });
  };
  const remove = (code: string) => {
    const index = draft.findIndex((w) => w.code === code);
    const next = removeWidget(draft, code);
    setDraft(next);
    setAnnounce(`Виджет «${label(code)}» убран со стола. Вернуть его можно через «Добавить виджет»`);
    const neighbour = next[Math.min(index, next.length - 1)];
    if (neighbour) setFocus({ code: neighbour.code, part: "handle" });
  };
  const add = (code: string) => {
    const item = byCode.get(code);
    if (!item) return;
    const next = addWidget(draft, code, item.size);
    setDraft(next);
    setCatalogOpen(false);
    say(code, next, `Виджет «${item.label}» добавлен`);
    setFocus({ code, part: "handle" });
  };
  const standard = () => {
    const next = standardLayout(payload?.standard.desktop.widgets ?? [], catalogCodes);
    setDraft(next);
    message.info("Показан стандартный вид. Нажмите «Сохранить», чтобы оставить его, или «Отмена».");
    setAnnounce("Показан стандартный вид рабочего стола, он ещё не сохранён");
  };

  const save = async (revision: number) => {
    setSaveError(null);
    try {
      await workspace.save.mutateAsync({ revision, desktop: { widgets: forSave(draft, kept) } });
      setConflict(null);
      finish();
      message.success("Рабочий стол сохранён");
      setAnnounce("Рабочий стол сохранён");
    } catch (error) {
      const current = conflictOf(error);
      if (current) setConflict(current);
      // UXW-16 / CW-19: the edited layout stays on the screen for another try; nothing is reported as saved
      else setSaveError(errorMessage(error));
    }
  };
  const loadTheirs = async () => {
    setConflict(null);
    finish();
    await workspace.refetch();
    setAnnounce("Загружен рабочий стол, сохранённый в другом окне");
  };

  // ---- the keyboard on the handle: arrows move, Enter or Space takes / puts, Escape returns (UXW-22) -------------
  const onHandleKey = (code: string) => (e: KeyboardEvent<HTMLButtonElement>) => {
    const direction = KEY_DIRECTIONS[e.key];
    if (direction) {
      e.preventDefault();
      if (keyMove.current?.code !== code) {
        keyMove.current = { code, origin: draft };
        setGrabbed(code);
      }
      step(code, direction, "handle");
      return;
    }
    if (e.key === "Escape" && keyMove.current?.code === code) {
      e.preventDefault();
      e.stopPropagation();
      setDraft(keyMove.current.origin);
      keyMove.current = null;
      setGrabbed(null);
      setAnnounce(`Перемещение отменено: виджет «${label(code)}» на прежнем месте`);
      setFocus({ code, part: "handle" });
      return;
    }
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (keyMove.current?.code === code) {
        keyMove.current = null;
        setGrabbed(null);
        say(code, draft, `Виджет «${label(code)}» поставлен`);
      } else {
        keyMove.current = { code, origin: draft };
        setGrabbed(code);
        setAnnounce(`Виджет «${label(code)}» взят. Стрелки — переместить, Enter — поставить, Escape — вернуть на место`);
      }
    }
  };

  // ---- the pointer on the handle: mouse, pen and finger; the rest of the card never starts a drag (UXW-21/23) -----
  const hit = (x: number, y: number, self: string): string | null => {
    for (const [code, el] of cells.current) {
      if (code === self) continue;
      const r = el.getBoundingClientRect();
      if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) return code;
    }
    return null;
  };
  const onPointerDown = (code: string) => (e: PointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0) return;
    drag.current = { code, pointerId: e.pointerId, x: e.clientX, y: e.clientY, active: false, origin: draft, over: null };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: PointerEvent<HTMLButtonElement>) => {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    if (!d.active) {
      if (Math.hypot(e.clientX - d.x, e.clientY - d.y) < 6) return;
      d.active = true;
      setDragging(d.code);
      setAnnounce(`Перетаскивание виджета «${label(d.code)}»: отпустите на нужном месте, Escape — вернуть`);
    }
    e.preventDefault();
    // near the top or bottom edge the page scrolls, so a widget can be carried to a place outside the screen
    if (e.clientY < 64) window.scrollBy(0, -24);
    else if (e.clientY > window.innerHeight - 64) window.scrollBy(0, 24);
    const over = hit(e.clientX, e.clientY, d.code);
    if (over === d.over) return;
    d.over = over;
    if (!over) return;
    // the dragged widget takes the place of the one under the pointer; the others shift, nothing overlaps (UXW-23)
    setDraft((current) => moveTo(current, current.findIndex((w) => w.code === d.code), current.findIndex((w) => w.code === over)));
  };
  const onPointerEnd = (cancelled: boolean) => (e: PointerEvent<HTMLButtonElement>) => {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    drag.current = null;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
    if (!d.active) return; // a plain press on the handle moves nothing
    setDragging(null);
    if (cancelled) {
      setDraft(d.origin);
      setAnnounce(`Перемещение отменено: виджет «${label(d.code)}» на прежнем месте`);
    } else {
      say(d.code, draftRef.current, `Виджет «${label(d.code)}» поставлен`);
    }
    setFocus({ code: d.code, part: "handle" });
  };

  const register = (key: string) => (el: HTMLElement | null) => {
    if (el) controls.current.set(key, el);
    else controls.current.delete(key);
  };

  if (workspace.isError) {
    return (
      <>
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          data-testid="desktop-load-error"
          message="Не удалось загрузить ваш рабочий стол — показан обычный вид"
          description={errorMessage(workspace.error)}
          action={
            <Button size="small" onClick={() => workspace.refetch()}>
              Повторить
            </Button>
          }
        />
        {fallback}
      </>
    );
  }
  if (!payload) {
    return (
      <>
        {header}
        <div data-testid="desktop-loading" aria-busy="true">
          <Skeleton active paragraph={{ rows: 6 }} />
        </div>
      </>
    );
  }

  const query = search.trim().toLowerCase();
  const offered = catalog.filter((c) => !query || c.label.toLowerCase().includes(query) || c.help.toLowerCase().includes(query));
  const draftLabels = (widgets: Placement[]) => widgets.filter((w) => byCode.has(w.code)).map((w) => label(w.code));

  return (
    <div className={`wms-desktop${editing ? " is-editing" : ""}`} data-testid="desktop" data-cabinet={cabinet}>
      {header}
      <div className="wms-sr-only" aria-live="polite" role="status" data-testid="desktop-live">
        {announce}
      </div>
      <span id={HINT_ID} className="wms-sr-only">
        Тяните за эту ручку мышью или пальцем. С клавиатуры: стрелки перемещают виджет, Enter ставит его, Escape возвращает на место.
      </span>
      <div className="wms-desktop-toolbar" data-testid="desktop-toolbar">
        {editing ? (
          <>
            <Tag color="gold" className="wms-desktop-mode">
              Настройка стола
            </Tag>
            <Typography.Text type="secondary" className="wms-desktop-hint">
              Тяните виджет за ручку <HolderOutlined /> или пользуйтесь кнопками. Ссылки в виджетах сейчас не открываются. Документы и данные не меняются.
            </Typography.Text>
            <Space wrap className="wms-desktop-actions">
              <Button icon={<PlusOutlined />} onClick={() => setCatalogOpen(true)} data-testid="desktop-add">
                Добавить виджет
              </Button>
              <Button icon={<UndoOutlined />} onClick={standard} data-testid="desktop-standard">
                Стандартный вид
              </Button>
              <Button onClick={cancel} data-testid="desktop-cancel">
                Отмена
              </Button>
              <Button type="primary" loading={workspace.save.isPending} onClick={() => save(payload.workspace.revision)} data-testid="desktop-save">
                Сохранить
              </Button>
            </Space>
          </>
        ) : (
          <>
            <div className="wms-desktop-extra">{toolbarExtra}</div>
            <Button icon={<AppstoreAddOutlined />} onClick={() => begin()} data-testid="desktop-customize">
              Настроить рабочий стол
            </Button>
          </>
        )}
      </div>
      {saveError && (
        <Alert
          type="error"
          showIcon
          closable
          onClose={() => setSaveError(null)}
          style={{ marginBottom: 12 }}
          data-testid="desktop-save-error"
          message="Рабочий стол не сохранён"
          description={`${saveError}. Ваша раскладка осталась на экране — нажмите «Сохранить» ещё раз.`}
        />
      )}

      {list.length === 0 ? (
        <div className="wms-desktop-empty" data-testid="desktop-empty">
          <Empty description="На рабочем столе нет виджетов. Добавьте нужные или верните стандартный вид — документы и данные от этого не меняются.">
            <Space wrap>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  if (!editing) begin();
                  setCatalogOpen(true);
                }}
                data-testid="desktop-empty-add"
              >
                Добавить виджет
              </Button>
              <Button
                icon={<UndoOutlined />}
                onClick={() => (editing ? standard() : begin(standardLayout(payload.standard.desktop.widgets, catalogCodes)))}
                data-testid="desktop-empty-standard"
              >
                Стандартный вид
              </Button>
            </Space>
          </Empty>
        </div>
      ) : (
        <div className="wms-desktop-grid" data-testid="desktop-grid">
          {list.map((w, index) => {
            const item = byCode.get(w.code);
            if (!item) return null;
            const view = render(w.code);
            const name = item.label;
            const classes = ["wms-widget", w.size === "full" ? "is-full" : "is-half"];
            if (dragging === w.code) classes.push("is-dragging");
            if (grabbed === w.code) classes.push("is-grabbed");
            return (
              <section
                key={w.code}
                ref={(el) => {
                  if (el) cells.current.set(w.code, el);
                  else cells.current.delete(w.code);
                }}
                className={classes.join(" ")}
                aria-labelledby={`desktop-title-${w.code}`}
                data-testid={`desktop-widget-${w.code}`}
                data-size={w.size}
              >
                <header className="wms-widget-head">
                  {editing && (
                    <button
                      type="button"
                      ref={register(`${w.code}:handle`)}
                      className="wms-widget-handle"
                      aria-label={`Переместить виджет «${name}»`}
                      aria-describedby={HINT_ID}
                      aria-pressed={grabbed === w.code}
                      data-testid={`desktop-handle-${w.code}`}
                      onKeyDown={onHandleKey(w.code)}
                      onPointerDown={onPointerDown(w.code)}
                      onPointerMove={onPointerMove}
                      onPointerUp={onPointerEnd(false)}
                      onPointerCancel={onPointerEnd(true)}
                    >
                      <HolderOutlined />
                    </button>
                  )}
                  <h2 className="wms-widget-title" id={`desktop-title-${w.code}`}>
                    {name}
                  </h2>
                  <HelpHint subject={name} text={item.help} />
                  {!editing && view.extra ? <div className="wms-widget-extra">{view.extra}</div> : null}
                </header>
                {editing && (
                  <div className="wms-widget-controls" role="group" aria-label={`Положение виджета «${name}»`}>
                    {MOVES.map((m) => (
                      <Button
                        key={m.dir}
                        size="small"
                        icon={m.icon}
                        ref={register(`${w.code}:${m.dir}`)}
                        disabled={!canMove(list, index, m.dir, columns)}
                        onClick={() => step(w.code, m.dir, m.dir)}
                        aria-label={`${m.label}: виджет «${name}»`}
                        data-testid={`desktop-${m.dir}-${w.code}`}
                      >
                        {m.label}
                      </Button>
                    ))}
                    {item.sizes.length > 1 && (
                      <Button
                        size="small"
                        icon={<ColumnWidthOutlined />}
                        ref={register(`${w.code}:size`)}
                        onClick={() => {
                          const next = setSize(draft, w.code, w.size === "full" ? "half" : "full");
                          setDraft(next);
                          setAnnounce(`Виджет «${name}»: ${w.size === "full" ? "половина ширины" : "во всю ширину"}`);
                          setFocus({ code: w.code, part: "size" });
                        }}
                        data-testid={`desktop-size-${w.code}`}
                      >
                        {w.size === "full" ? "Половина ширины" : "Во всю ширину"}
                      </Button>
                    )}
                    <Button size="small" icon={<EyeInvisibleOutlined />} onClick={() => remove(w.code)} aria-label={`Убрать виджет «${name}» со стола`} data-testid={`desktop-remove-${w.code}`}>
                      Убрать
                    </Button>
                  </div>
                )}
                <WidgetBody editing={editing}>{view.body}</WidgetBody>
              </section>
            );
          })}
        </div>
      )}

      <Modal
        open={catalogOpen}
        title="Добавить виджет"
        onCancel={() => setCatalogOpen(false)}
        footer={null}
        destroyOnHidden
        width={640}
      >
        <div data-testid="desktop-catalog">
          <Input.Search allowClear autoFocus placeholder="Найти виджет по названию" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="desktop-catalog-search" />
          <Typography.Paragraph type="secondary" style={{ margin: "10px 0" }}>
            Здесь только виджеты, доступные вашей роли. Виджет показывает данные по вашим правам и новых прав не даёт.
          </Typography.Paragraph>
          <List
            dataSource={offered}
            locale={{ emptyText: "Ничего не найдено" }}
            renderItem={(item) => {
              const added = draft.some((w) => w.code === item.code);
              return (
                <List.Item
                  data-testid={`desktop-catalog-item-${item.code}`}
                  actions={[
                    added ? (
                      <Tag key="added" data-testid={`desktop-catalog-added-${item.code}`}>
                        Уже на столе
                      </Tag>
                    ) : (
                      <Button key="add" type="primary" onClick={() => add(item.code)} data-testid={`desktop-catalog-add-${item.code}`}>
                        Добавить
                      </Button>
                    ),
                  ]}
                >
                  <List.Item.Meta
                    title={
                      <Space size={6} wrap>
                        <span>{item.label}</span>
                        <Tag>{item.size === "full" ? "во всю ширину" : "половина ширины"}</Tag>
                        {item.default && <Tag color="blue">в стандартном виде</Tag>}
                      </Space>
                    }
                    description={
                      <>
                        <div>{item.help}</div>
                        {previews[item.code] && <div className="wms-catalog-preview">Покажет: {previews[item.code]}</div>}
                      </>
                    }
                  />
                </List.Item>
              );
            }}
          />
        </div>
      </Modal>

      <Modal
        open={!!conflict}
        title="Рабочий стол изменён в другом окне"
        onCancel={() => setConflict(null)}
        footer={[
          <Button key="load" onClick={loadTheirs} data-testid="desktop-conflict-load">
            Загрузить новый вид
          </Button>,
          <Button key="mine" type="primary" loading={workspace.save.isPending} onClick={() => conflict && save(conflict.revision)} data-testid="desktop-conflict-mine">
            Сохранить мой вариант
          </Button>,
        ]}
      >
        <Typography.Paragraph data-testid="desktop-conflict">Пока вы настраивали стол, его или оформление сохранили в другом окне или на другом устройстве. Ничего не перезаписано.</Typography.Paragraph>
        {conflict && (
          <Typography.Paragraph type="secondary">
            Там сейчас: {draftLabels(conflict.desktop.widgets).join(", ") || "пустой стол"}.
            <br />
            Ваш вариант: {draftLabels(draft).join(", ") || "пустой стол"}.
          </Typography.Paragraph>
        )}
      </Modal>
    </div>
  );
}
